import React, { useCallback, useRef } from 'react'
import { get } from 'lodash-es'
import { FormattedMessage } from 'react-intl'
import { useDispatch } from 'react-redux'
import classnames from 'classnames'

import { TtButton as Button } from '@23/react'

import {
  type ChatId,
  getChat,
  isAudienceChatEnabled,
  setSelectedChat,
  useSelectedChatId,
} from '../../../../../Chat/blocs/chatVisibility'
import { useAppSelector } from '../../../../../redux-types'
import { useWebinarColors } from '../../../../hooks/useWebinarColors'
import { generateActions } from '../../../../utils/bundleFeature'
import { useLiveDataQuery } from '../../../../../global/blocs/useLiveData'
import useFeatures from '../../../../hooks/useFeatures'

import './WebinarChatSelectorComponent.scss'

const { actionNames: chatActionNames } = generateActions(
  'webinar-sidebar-chat',
  ['SET_SHOULD_SCROLL']
)

const { actionNames: speakerChatActionNames } = generateActions(
  'webinar-sidebar-speaker-chat',
  ['SET_SHOULD_SCROLL']
)

const formatUnread = (n: number) => {
  return n > 99 ? '99+' : `${n}`
}

function updateMessages(
  selector: typeof useAppSelector,
  namespace:
    | 'feature-webinar-sidebar-chat'
    | 'feature-webinar-sidebar-speaker-chat',
  count: { total: number; read: number }
): Record<'total' | 'read', number> {
  const [currentTotalMessages, isLoading] = selector(
    (state) => {
      const chat = state[namespace]?.chat
      return [chat?.comments.size, chat?.isLoading]
    },
    ([a1, b1], [a2, b2]) => a1 === a2 && b1 === b2
  )
  if (count.total === -2) {
    if (isLoading) return { total: -1, read: -1 }
    return { total: -2, read: -2 }
  }
  const newCount = count
  if (!isLoading) {
    if (count.total === -1) {
      newCount.read = currentTotalMessages
    }
    newCount.total = currentTotalMessages
  }
  return newCount
}

const WebinarChatSelectorComponent = () => {
  const theme = useWebinarColors()
  const dispatch = useDispatch()
  const selectedChatId = useSelectedChatId()
  const { data: liveData } = useLiveDataQuery()
  const { HasSpeakerChatEnabled } = useFeatures()

  const audienceChatEnabled = isAudienceChatEnabled(
    get(liveData, 'comment_mode', '')
  )

  const readMessages = useRef<Record<ChatId, { total: number; read: number }>>({
    chat: {
      total: -2,
      read: -2,
    },
    'speaker-chat': {
      total: -2,
      read: -2,
    },
  })

  readMessages.current.chat = updateMessages(
    useAppSelector,
    'feature-webinar-sidebar-chat',
    readMessages.current.chat
  )
  readMessages.current['speaker-chat'] = updateMessages(
    useAppSelector,
    'feature-webinar-sidebar-speaker-chat',
    readMessages.current['speaker-chat']
  )

  const selRoom = selectedChatId
  readMessages.current[selRoom].read = readMessages.current[selRoom].total

  const onChange = useCallback(
    (id: ChatId) => {
      readMessages.current[id].read = readMessages.current[id].total
      setSelectedChat(id)
      switch (id) {
        case 'chat':
          dispatch({
            type: chatActionNames.SET_SHOULD_SCROLL,
            shouldScroll: true,
          })
          break
        case 'speaker-chat':
          dispatch({
            type: speakerChatActionNames.SET_SHOULD_SCROLL,
            shouldScroll: true,
          })
          break
        default:
      }
    },
    [dispatch]
  )

  const tabsToShow = []
  if (audienceChatEnabled) {
    tabsToShow.push({ chat: getChat('chat'), id: 'chat' } as const)
  }
  if (HasSpeakerChatEnabled) {
    tabsToShow.push({
      chat: getChat('speaker-chat'),
      id: 'speaker-chat',
    } as const)
  }

  return (
    <div className="webinar-chat-selector-component">
      {tabsToShow.map(({ chat, id }) => (
        <Button
          className={classnames('webinar-chat-selector-button', {
            'webinar-chat-selector-button-selected': chat.isSelected,
          })}
          size="small"
          onClick={() => {
            onChange(id)
          }}
          key={id}
          role="tab"
          aria-selected={chat.isSelected}
        >
          {id === 'speaker-chat' ? (
            <FormattedMessage defaultMessage="Speaker Chat" />
          ) : (
            <FormattedMessage defaultMessage="Audience Chat" />
          )}

          {readMessages.current[id].total >= 0 &&
            readMessages.current[id].total > readMessages.current[id].read && (
              <span
                slot="suffix"
                className="notification-dot"
                style={{
                  backgroundColor: theme.accent.color,
                  color: theme.accent.contrast.color,
                }}
              >
                {formatUnread(
                  readMessages.current[id].total - readMessages.current[id].read
                )}
              </span>
            )}
        </Button>
      ))}
    </div>
  )
}

export { WebinarChatSelectorComponent }
